import { i18n } from '@international/mastodon-i18n';
import {
  DISPUTE_SECTIONS,
  AUTO_DISPUTE_SCREENINGS,
} from '../../../../constants';
import { isInternational } from '../../../../lib/helpers';
import { dateIfDate } from './formatting';

import internationalDisputes from '../../../../data/international_disputes.json';
import disputes from '../../../../data/disputes.json';
import en from '../../../../lib/translations/en.json';

// The dispute data files only hold structure (stable keys, Zendesk ids,
// labels, flags). All display strings live in the `disputes.tree` namespace
// of the translation files, which the translation bot keeps in sync across
// locales. English (en.json) is the canonical source: section/article `name`
// is always the English string and doubles as the stable identifier used for
// form values and the dispute-intake payload, which the backend matches
// against English section names. Localized strings are only used for display.
const englishTree = en.disputes.tree;

const localizedTreeString = path => i18n.getStr(`disputes.tree.${path}`);

// A data file entry without matching en.json strings is a content bug
// (covered by the parity test in __tests__/screenings.test.ts); fall back to
// the raw key rather than crashing the whole dispute modal.
const buildArticle = (sectionKey, articleData) => {
  const { key, ...structure } = articleData;
  const strings = englishTree[sectionKey]?.articles?.[key] ?? {};
  const path = `${sectionKey}.articles.${key}`;

  const article = {
    ...structure,
    name: strings.title ?? key,
    title:
      strings.title !== undefined ? localizedTreeString(`${path}.title`) : key,
  };
  ['body', 'newBody', 'bodyAutoDispute'].forEach(field => {
    if (strings[field] !== undefined) {
      article[field] = localizedTreeString(`${path}.${field}`);
    }
  });
  return article;
};

const buildSection = sectionData => {
  const { key, articles, ...structure } = sectionData;
  const strings = englishTree[key] ?? {};
  return {
    ...structure,
    name: strings.name ?? key,
    displayName:
      strings.name !== undefined ? localizedTreeString(`${key}.name`) : key,
    articles: articles.map(article => buildArticle(key, article)),
  };
};

const loadDisputeSections = () => {
  const intSections = internationalDisputes.sections.map(buildSection);

  if (isInternational()) {
    return intSections;
  }

  const domesticSections = disputes.sections.map(buildSection);
  // concat sections and remove duplicates
  return [...intSections, ...domesticSections].filter(
    (v, i, a) => a.findIndex(v2 => v2.name === v.name) === i,
  );
};

const getDisputeTree = () => loadDisputeSections();

const ALL_SCREENINGS = [
  'ssnTrace',
  'evictionSearch',
  'sexOffenderSearch',
  'terroristWatchlistSearch',
  'healthcareSanctionsSearches',
  'internationalCriminalSearches',
  'arrestSearch',
  'nationalCriminalSearch',
  'stateCriminalSearches',
  'pointerStateCriminalSearches',
  'federalCriminalSearch',
  'federalDistrictCriminalSearches',
  'federalDistrictCivilSearches',
  'federalCivilSearch',
  'countyCriminalSearches',
  'countyCivilSearches',
  'clearinghouseSearch',
  'municipalCriminalSearches',
  'childAbuseSearches',
  'motorVehicleReport',
  'employmentVerification',
  'educationVerification',
  'personalReferenceVerifications',
  'professionalReferenceVerifications',
  'abuseRegistrySearch',
  'socialMediaScreenings',
  'facisSearch',
  'creditReport',
  'professionalLicenseVerifications',
  'internationalGlobalWatchlistSearch',
  'internationalEducationVerification',
  'internationalEmploymentVerification',
  'internationalCriminalSearchesV2',
  'internationalCanadaBcEnhancedCriminalSearch',
  'internationalAdverseMediaSearches',
  'internationalIdentityDocumentValidation',
  'internationalMotorVehicleReports',
  'safetyAllianceSearch',
];

const validScreenings = report => {
  return ALL_SCREENINGS.filter(screening => {
    return (
      report[screening] &&
      (!!report[screening].length || !!report[screening].id)
    );
  });
};

const considerScreenings = report => {
  return validScreenings(report).filter(screening => {
    if (report[screening].length) {
      return report[screening].some(search => search.status === 'consider');
    }
    return report[screening].status === 'consider';
  });
};

const disputableScreenings = report => {
  return considerScreenings(report).filter(screening => {
    return report[screening].isDisputable !== false;
  });
};

const allScreenings = () => ALL_SCREENINGS;

const getSections = screenings => {
  const titles = screenings.map(
    screening => DISPUTE_SECTIONS[screening]?.title,
  );
  return getDisputeTree().filter(section => titles.indexOf(section.name) >= 0);
};

const filterOutMvrScreening = screenings => {
  return screenings.filter(screening => screening !== 'motorVehicleReport');
};

const getDisputeSections = report => {
  let screenings = considerScreenings(report);
  screenings = filterOutMvrScreening(screenings);
  return getSections(screenings);
};

// Localized display name for a canonical (English) section name.
const getSectionDisplayName = sectionName => {
  const section = getDisputeTree().find(s => s.name === sectionName);
  return section?.displayName ?? sectionName;
};

// Localized display title for a canonical (English) article name.
const getArticleDisplayTitle = (sectionName, articleName) => {
  return getArticle(sectionName, articleName).title ?? articleName;
};

const getSection = sectionName => {
  return Object.keys(DISPUTE_SECTIONS).find(
    section => DISPUTE_SECTIONS[section].title === sectionName,
  );
};

const getSearches = (report, sectionNames) => {
  const sections = [];
  sectionNames.forEach(item => {
    const reportSection = report[getSection(item)];

    if (!reportSection) {
      return;
    }

    Array.isArray(reportSection)
      ? sections.push(...reportSection)
      : sections.push(reportSection);
  });
  return sections;
};

const getSearchesBySectionAndResult = (report, sections, result) => {
  return getSearches(report, sections).filter(
    search => search?.status === result,
  );
};

const requiresCharges = sectionName => {
  const disputeTree = getDisputeTree();
  const sectionObject = disputeTree.find(
    section => section.name === sectionName,
  );
  const sectionLabels = sectionObject ? sectionObject.labels : [];

  return sectionLabels.some(label => label.indexOf('charges') !== -1);
};

const getArticles = sectionName => {
  if (!sectionName) return [];

  return getDisputeTree().find(section => section.name === sectionName)
    ?.articles;
};

const getArticle = (sectionName, articleName) => {
  if (!sectionName || !articleName) return {};

  const articles = getArticles(sectionName);
  return articles.find(article => article.name === articleName) || {};
};

const requiresTicket = (sectionName, articleName) => {
  const article = getArticle(sectionName, articleName);
  return (article.labels ?? []).some(label => label.indexOf('reason_') !== -1);
};

const requiresField = (sectionName, articleName) => {
  const article = getArticle(sectionName, articleName);

  return (article.labels ?? []).some(
    label => label.indexOf('field_required') !== -1,
  );
};

const allSearchRecords = search => {
  return search?.records.concat(
    search?.filteredByPositiveAdjudicationRecords || [],
  );
};

const getFieldOptions = sectionName => {
  switch (sectionName) {
    case 'Arrest Search':
      return ['Charge Name', 'Booking Information'];
    case 'Abuse Registry Search':
      return [
        'Subject Full Name',
        'Date of Birth',
        'Registry State',
        'Registry Authority',
        'Additional Notes',
      ];
    case 'Social Media Search':
      return ['Flagged Behaviors'];
    case 'SSN Verification':
      return ['Name', 'Date of Birth', 'SSN'];
    default:
      return ['Charge Name', 'Charge Type', 'Dates', 'Disposition', 'Sentence'];
  }
};

export const getFirstRecordCharge = record => {
  if (!record || !Array.isArray(record.charges)) return undefined;
  const allCharges = record.charges.concat(
    record.filteredByPositiveAdjudicationCharges || [],
    record.chargesHiddenByAssess || [],
  );
  return allCharges[0];
};

export const TAIWAN_ALIAS_ACCOUNTS = [
  '6f274ba12429f2045a001b63', // keeptruckin-4e47c69822
  'e9b18155a51edab376053be8', // checkr-staging-test
  '4a6c808cbf4cfe7a2197e080',
];

const getCountryName = (country, accountId = '') => {
  if (!country) return 'N/A';

  if (
    TAIWAN_ALIAS_ACCOUNTS.includes(accountId) &&
    i18n.getLocale().substring(0, 2) === 'en' &&
    country === 'TW'
  ) {
    return 'Taiwan';
  }

  return `${i18n.getCountry(country)}`;
};

const formatCountry = (country, accountId = '') => {
  const aliasedCountry = getCountryName(country, accountId);

  return `${aliasedCountry} (${country})`;
};

const formatDistrictSearches = districtSearches => {
  if (!districtSearches) return 'N/A';

  const districts = districtSearches.map(item => item.courtJurisdiction);

  return districts.filter(n => n).join(', ');
};

const formatSearchTitle = (search, accountId) => {
  const { court, county, state, districtSearches, country } = search;

  if (country) {
    return formatCountry(country, accountId);
  }

  if (districtSearches) {
    return formatDistrictSearches(districtSearches);
  }

  return [court, county, state].filter(n => n).join(', ');
};

const getChargeTitle = record => {
  if (record && record.incidentId) {
    return record.category || 'Unknown incident';
  }
  const firstCharge = getFirstRecordCharge(record);
  return firstCharge ? firstCharge.charge : 'Unknown charge';
};

const getChargeDate = record => {
  const firstCharge = getFirstRecordCharge(record);
  return firstCharge ? dateIfDate(firstCharge.offenseDate) : null;
};

const generateDisputeReason = (article, section) => {
  const articleObj = getArticle(section, article);

  return articleObj.labels?.find(
    labelParam => labelParam.indexOf('reason_') !== -1,
  );
};
const isInfoScreen = (sectionName, articleName) => {
  const article = getArticle(sectionName, articleName);
  return (article.labels ?? []).some(label =>
    label.includes('information_screen'),
  );
};

const isAutoDisputeScreening = currentSection => {
  return AUTO_DISPUTE_SCREENINGS.some(
    screening => screening === currentSection,
  );
};

const getMotorVehicleRecordLengths = licenseReport => {
  if (licenseReport) {
    const fields = [
      'restrictions',
      'violations',
      'accidents',
      'suspensions',
      'endorsements',
    ];

    return fields.reduce((results, field) => {
      const key = `${field}Length`;
      return {
        ...results,
        [key]: licenseReport[field]?.length || 0,
      };
    }, {});
  }
  return {};
};

export {
  allSearchRecords,
  validScreenings,
  considerScreenings,
  disputableScreenings,
  filterOutMvrScreening,
  getSections,
  getDisputeSections,
  getArticles,
  getArticle,
  getMotorVehicleRecordLengths,
  allScreenings,
  getSection,
  getSectionDisplayName,
  getArticleDisplayTitle,
  getSearches,
  getSearchesBySectionAndResult,
  getCountryName,
  formatCountry,
  formatSearchTitle,
  getChargeTitle,
  getChargeDate,
  requiresCharges,
  requiresField,
  getFieldOptions,
  requiresTicket,
  generateDisputeReason,
  isInfoScreen,
  isAutoDisputeScreening,
};
